/**
 * O que a moldura entrega a cada aba: a cliente aberta e o lead (atendimento) aberto.
 *
 * É o contrato publicado pela frente A3 em `progresso/PROGRESSO-A3.md` e seguido pelas 16 abas:
 * `<AbaX idCliente={id} ficha={{ cliente, lead }} />`. No legado são as duas variáveis globais
 * `clienteSelecionado` e `leadAdminSelecionado` (`admin.html:3314-3315`), lidas por toda aba.
 */

// O escopo de leitura e gravação da ficha, como o pacote de dados o recebe.
import type { Escopo } from '@studiolu/dados';

/** A cliente aberta: os campos do objeto da lista que a ficha usa (`admin.html:5944-5948`). */
export interface ClienteDaFicha {
  readonly user_id: string;
  readonly nome_completo: string | null;
  readonly email: string | null;
  readonly telefone: string | null;
}

/**
 * O lead aberto: as colunas da escolha "Qual atendimento?" (`admin.html:5878`) e a origem.
 *
 * A ORIGEM É OPCIONAL DE PROPÓSITO, e a ausência dela é informação: no legado o objeto do lead vem
 * com todas as colunas quando a ficha é aberta pelo Kanban (`select('*')`, `admin.html:6511`) ou
 * pelo compromisso (`4540`), e com quatro colunas quando vem da lista de clientes (`5878`). O
 * Histórico escreve "Lead criada · origem: X" só quando ela existe (`5663-5666`), e é assim que a
 * mesma cliente mostra a origem aberta pelo funil e não mostra aberta pela lista.
 */
export interface LeadAberto {
  readonly id: number;
  readonly produtos: readonly string[];
  readonly status: string;
  readonly data_criacao: string;
  readonly origem?: string | null;
}

/** A ficha aberta: a cliente e, quando existe, o lead. `lead` nulo é a cliente sem atendimento. */
export interface FichaAberta {
  readonly cliente: ClienteDaFicha;
  readonly lead: LeadAberto | null;
}

/**
 * As propriedades de toda aba: o id da cliente (contrato antigo) e a ficha aberta.
 *
 * A CARGA DA ABA NÃO É PROPRIEDADE, e sim contexto (`comum-ficha/carregador.tsx`): a aba adere ao
 * contrato chamando `useCarregadorDaAba` no componente que lê, e não precisa repassar propriedade
 * nenhuma pelos componentes de dentro. É o contrato que faz a moldura esperar a leitura antes de
 * mostrar a aba e reler quando a pessoa toca de novo no botão aceso, como o legado (`5086-5091`).
 */
export interface PropsDaAba {
  readonly idCliente: string;
  // Opcional só para o typecheck: sem ela a aba diz que não há ficha aberta e não consulta nada.
  readonly ficha?: FichaAberta;
}

/** O escopo de uma ficha: a cliente sempre, o lead quando existe (`comEscopo`, `admin.html:3320`). */
export function escopoDaFicha(ficha: FichaAberta): Escopo {
  return { userId: ficha.cliente.user_id, leadId: ficha.lead?.id ?? null };
}
